import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'umi';
import { useDebounceFn, useSetState } from 'ahooks';
import CommonBubble from '@home/components/CommonBubble';
import type { TTableActionType, ITableActionConfig } from './types';
import copySuccessIcon from '@/pages/aigc/assets/svg/agent-copy-success.svg';

/**
 * 表格操作组件 Props
 */
export interface ITableActionsProps {
  /** 自定义操作配置数组（必填） */
  actions: ITableActionConfig[];
}

/**
 * 表格操作组件 - 纯展示组件，通过 props 接收 actions 配置
 * @param actions - 操作配置数组（必填）
 */
const TableActions: React.FC<ITableActionsProps> = ({ actions }) => {
  const { formatMessage } = useIntl();
  const [state, setState] = useSetState<{
    /**
     * 各 action 的激活态映射（key 按需写入）
     * @description
     * - 仅在 onClick 成功后写入 true，2s 后再 delete
     * - 使用 Partial 是因为：1) 初始无任何激活；2) TTableActionType 后续扩展（如 collapse）无需同步初始化
     */
    activeActions: Partial<Record<TTableActionType, boolean>>;
    hoveredAction: TTableActionType | null;
  }>({
    activeActions: {},
    hoveredAction: null,
  });
  const timersRef = useRef<Map<TTableActionType, NodeJS.Timeout>>(new Map());

  // 组件卸载时清理所有定时器
  useEffect(() => () => {
    timersRef.current.forEach((timer) => {
      if (timer) clearTimeout(timer);
    });
    timersRef.current.clear();
  }, []);

  /**
   * 清理指定操作的定时器
   */
  const clearTimer = useCallback((actionType: TTableActionType) => {
    const timer = timersRef.current.get(actionType);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(actionType);
    }
  }, []);

  const setTimer = useCallback((actionType: TTableActionType, callback: () => void, delay: number) => {
    // 先清理旧定时器
    clearTimer(actionType);

    // 设置新定时器
    const timer = setTimeout(() => {
      callback();
      timersRef.current.delete(actionType);
    }, delay);

    timersRef.current.set(actionType, timer);
  }, [clearTimer]);

  /**
   * 计算每个操作的 tooltip visible 状态
   * - copy: 激活状态或悬停时显示（激活状态会持续2秒）
   */
  const getActionVisible = useCallback((action: ITableActionConfig) => {
    const isActive = state.activeActions[action.type];
    const isHovered = state.hoveredAction === action.type;

    // copy 操作：激活或悬停时都显示 tooltip
    if (action.type === 'copy') {
      return isActive || isHovered;
    }

    // 其他操作：只在激活或悬停时显示
    return isHovered;
  }, [state.activeActions, state.hoveredAction]);

  /**
   * 处理操作点击（核心逻辑）
   * @description
   * - 调用外部传入的 onClick 处理器
   * - 自动管理操作状态（激活状态、成功提示等）
   * - 根据 autoHideAfterSuccess 配置决定是否2秒后自动清除激活状态
   */
  const runActionClick = useCallback(async (action: ITableActionConfig) => {
    try {
      if (action.onClick) {
        // 调用业务处理函数
        const success = await action.onClick();

        // 如果操作失败 / 是 UI 切换型 action（success===false），不设置激活状态
        if (success === false) {
          return;
        }

        // 操作成功后，设置该操作为激活状态（高亮图标）
        setState((prev) => ({
          activeActions: { ...prev.activeActions, [action.type]: true }
        }));

        // 2秒后自动取消高亮状态
        setTimer(action.type, () => {
          setState((prev) => {
            const newActiveActions = { ...prev.activeActions };
            delete newActiveActions[action.type];
            return { activeActions: newActiveActions };
          });
        }, 2000);
      }
    } catch (error) {
      console.error(`${action.type} 操作失败:`, error);
    }
  }, [setState, setTimer]);

  // 默认走 500ms 防抖，避免 copy/download 等副作用操作被连点
  const { run: debouncedActionClick } = useDebounceFn(runActionClick, {
    leading: true,
    wait: 500,
  });

  /**
   * 派发点击：UI 切换型 action（instant=true）即时执行；其他默认走防抖
   */
  const handleActionClick = useCallback((action: ITableActionConfig) => {
    if (action.instant) {
      void runActionClick(action);
      return;
    }
    debouncedActionClick(action);
  }, [runActionClick, debouncedActionClick]);

  /**
   * 获取操作提示内容
   * - copy: 激活时显示成功提示，悬停时显示默认提示
   * - download: 只在悬停时显示默认提示
   */
  const getTooltipContent = useCallback((action: ITableActionConfig) => {
    const isActive = state.activeActions[action.type];
    const hover = state.hoveredAction === action.type;
    if (action?.type === 'copy') {
      if (!isActive && !hover) {
        return '';
      }
    }
    if (isActive && action.successTooltipKey) {
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <img src={copySuccessIcon} alt="" style={{ width: 16, height: 16, marginRight: 5 }} />
          {formatMessage({ id: action.successTooltipKey })}
        </span>
      );
    }

    // 默认显示普通提示
    return formatMessage({ id: action.tooltipKey });
  }, [state.activeActions, state.hoveredAction, formatMessage]);

  /**
   * 获取当前显示的图标
   */
  const getCurrentIcon = useCallback((action: ITableActionConfig) => {
    const isActive = state.activeActions[action.type];
    const isHovered = state.hoveredAction === action.type || isActive;
    return isHovered && action.activeIcon ? action.activeIcon : action.icon;
  }, [state.activeActions, state.hoveredAction]);

  /**
   * 处理鼠标移入
   */
  const handleMouseEnter = useCallback((actionType: TTableActionType) => {
    setState({ hoveredAction: actionType });
  }, [setState]);

  /**
   * 处理鼠标移出
   */
  const handleMouseLeave = useCallback(() => {
    setState({ hoveredAction: null });
  }, [setState]);

  return (
    // 禁止拖拽
    <div className="dba-table-actions" contentEditable={false}>
      {actions.map((action) => (
        <CommonBubble
          key={action.type}
          placement="top"
          dark
          visible={getActionVisible(action)}
          content={getTooltipContent(action)}
          overlayClassName='dba-nav-menu-overlay'
        >
          <span
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            className="dba-table-actions-icon"
            onClick={() => handleActionClick(action)}
            onMouseEnter={() => handleMouseEnter(action.type)}
            onMouseLeave={handleMouseLeave}
          >
            <img src={getCurrentIcon(action)} alt="" />
          </span>
        </CommonBubble>
      ))}
    </div>
  );
};

export default React.memo(TableActions);
